import React from 'react';

interface MonthlyBreakdown {
  month: string;
  amount: number;
}

interface WorkloadCostItem {
  workload_id: string;
  workload_name: string;
  total_spent: number;
  trend_pct: number | null;
  project_names: string[];
  monthly_breakdown?: MonthlyBreakdown[];
}

interface WorkloadCostBreakdownProps {
  data: WorkloadCostItem[] | null;
  loading: boolean;
}

const formatCurrency = (amount: number | null | undefined) => {
  if (amount == null) return 'N/A';
  return `$${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatMonth = (monthStr: string) => {
  // "2026-01" -> "Jan"
  const [year, month] = monthStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleString('en-US', { month: 'short' });
};

const WorkloadCostBreakdown: React.FC<WorkloadCostBreakdownProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Workload Cost Breakdown
        </h3>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  const workloads = data || [];

  // Collect all unique months across all workloads, sorted
  const allMonths = Array.from(
    new Set(workloads.flatMap(w => (w.monthly_breakdown || []).map(m => m.month)))
  ).sort((a, b) => a.localeCompare(b));

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Workload Cost Breakdown
      </h3>

      {workloads.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          No workload cost data available
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Workload
                </th>
                {allMonths.map(month => (
                  <th key={month} className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {formatMonth(month)}
                  </th>
                ))}
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Total
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  MoM Trend
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Projects
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {workloads.map((workload) => {
                // Build a month->amount lookup for this workload
                const monthMap: Record<string, number> = {};
                (workload.monthly_breakdown || []).forEach(m => {
                  monthMap[m.month] = m.amount;
                });

                return (
                  <tr key={workload.workload_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-3 py-4 text-sm">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        {workload.workload_name}
                      </span>
                    </td>
                    {allMonths.map(month => (
                      <td key={month} className="px-3 py-4 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                        {monthMap[month] != null ? formatCurrency(monthMap[month]) : <span className="text-gray-300 dark:text-gray-600">--</span>}
                      </td>
                    ))}
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-right">
                      <span className="text-gray-900 dark:text-white font-medium">
                        {formatCurrency(workload.total_spent)}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-right">
                      {workload.trend_pct != null ? (
                        <span className={`font-medium ${
                          workload.trend_pct < 0
                            ? 'text-green-600 dark:text-green-400'
                            : workload.trend_pct > 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-500 dark:text-gray-400'
                        }`}>
                          {workload.trend_pct > 0 ? '+' : ''}{workload.trend_pct.toFixed(1)}%
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">--</span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm">
                      <div className="flex flex-wrap gap-1">
                        {(workload.project_names || []).length > 0 ? (
                          workload.project_names.map((name, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            >
                              {name}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500 text-xs">--</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default WorkloadCostBreakdown;
